import { DifficultyChip, QuestionTypeChip } from '@exam-management/components';
import { QuestionGroupType } from '@exam-management/graphql';
import {
  Chip,
  EllipsisText,
  Text,
  type GridColDef,
  IconButton,
  DeleteOutlineIcon,
} from '@shared/components';

export interface TaskManagementQuestionListType {
  id: string;
  taskId: string | null;
  qBankStructureId: string | null;
  order: number;
  code: string;
  chapterName: string;
  questionGroupType: QuestionGroupType;
  type: string;
  itemSetName?: string;
  subQuestionCount?: number;
  difficulty: 'easy' | 'normal' | 'hard' | '-';
  questionGroupId?: string | null;
  questionIds?: string[];
  createdBy: {
    fullName: string;
    email: string;
  };
}

export interface CreateColumnsOptions {
  onRemove: (row: TaskManagementQuestionListType) => void;
}

export const createColumns = (
  options: Readonly<CreateColumnsOptions>,
): GridColDef<TaskManagementQuestionListType>[] => [
  {
    field: 'order',
    headerName: 'No',
    width: 55,
    sortable: false,
    renderCell: (params) => <Text variant='body2'>{params.row.order}</Text>,
  },
  {
    field: 'code',
    headerName: 'QID',
    sortable: false,
    width: 200,
    renderCell: (params) => <Text variant='body2'>{params.row.code}</Text>,
  },
  {
    field: 'chapterName',
    headerName: 'Location',
    sortable: false,
    flex: 1,
    renderCell: (params) => <EllipsisText variant='body2'>{params.row.chapterName}</EllipsisText>,
  },
  {
    field: 'questionType',
    headerName: 'Question Type',
    sortable: false,
    width: 130,
    renderCell: (params) => {
      if (params.row.questionGroupType === QuestionGroupType.Single) {
        return <QuestionTypeChip type='single' />;
      }
      return (
        <Chip
          label={<Text variant='captionSemibold'>Item Set: {params.row.subQuestionCount}Q</Text>}
          size='small'
          sx={(theme) => ({
            backgroundColor: theme.palette.info.light,
          })}
        />
      );
    },
  },
  {
    field: 'itemSetName',
    headerName: 'Item Set Name',
    sortable: false,
    width: 150,
    renderCell: (params) => <Text variant='body2'>{params.row.itemSetName}</Text>,
  },
  {
    field: 'type',
    headerName: 'Type',
    sortable: false,
    width: 100,
    renderCell: (params) => (
      <Chip
        label={<Text variant='captionSemibold'>{params.row.type}</Text>}
        size='small'
        sx={(theme) => ({ backgroundColor: theme.palette.grey[200] })}
      />
    ),
  },
  {
    field: 'difficulty',
    headerName: 'Difficulty',
    sortable: false,
    width: 100,
    renderCell: ({ row }) => {
      const { difficulty } = row;
      if (difficulty === '-') {
        return <Text variant='body2'>-</Text>;
      }
      return <DifficultyChip type={difficulty} />;
    },
  },
  {
    field: 'createdBy',
    headerName: 'Created by',
    sortable: false,
    width: 125,
    renderCell: ({ row }) => (
      <EllipsisText maxLines={2} variant='body2'>
        {row.createdBy.fullName}
      </EllipsisText>
    ),
  },
  {
    field: 'moreAction',
    headerName: '',
    sortable: false,
    width: 40,
    renderCell: ({ row }) => (
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          options.onRemove(row);
        }}
      >
        <DeleteOutlineIcon sx={(theme) => ({ color: theme.palette.grey[500] })} />
      </IconButton>
    ),
  },
];
